// Internal operations follow the existing Korean administrator UI. The factory/customer UI is localized in shared copy.
import { Fragment, useState } from 'react'
import { Link } from 'react-router'
import { manufacturingStatusLabel, type ManufacturingRecord } from '@repo/shared'
import { adminApi, json, request } from '../api'
import { useGetToken } from '../lib/auth'
import { fmtTime } from '../lib/format'
import {
  useManufacturingFeature,
  useManufacturingState,
  manufacturingAction,
} from '../lib/manufacturing-api'
import { WorkflowForm, WorkflowTable, type WorkflowField } from '../components/manufacturing-forms'

export function ManufacturingPage() {
  const enabled = useManufacturingFeature()
  if (enabled === null) return null
  if (!enabled) return <p>제조 협력 접수가 꺼져 있습니다. 설정과 서버 준비 상태를 확인하세요.</p>
  return <ManufacturingConsole />
}

export function ManufacturingConsole() {
  const getToken = useGetToken(),
    { state, error, reload, mutate, mutating, mutationError } = useManufacturingState('operator')
  const [selected, setSelected] = useState(
    () => new URLSearchParams(location.search).get('request') ?? '',
  )
  const data = state?.workspace?.data,
    pending =
      mutating ||
      state?.jobs.some((j) => j.status === 'queued' || j.status === 'processing') === true
  if (error)
    return (
      <div role="alert">
        공장 관리 정보를 읽지 못했습니다. 권한과 연결 상태를 확인하세요.
        <button
          type="button"
          className="block underline"
          onClick={() => void reload().catch(() => undefined)}
        >
          다시 불러오기
        </button>
      </div>
    )
  if (!state) return null
  const props = {
    disabled: pending,
    submitLabel: '검토한 작업 접수',
    queuedLabel: '작업을 접수했습니다. 아래 처리 결과가 갱신될 때까지 기다려 주세요.',
    errorLabel: '처리하지 못했습니다. 입력과 최신 상태를 다시 확인하세요.',
  }
  const action = (body: Record<string, unknown>) =>
    mutate(() => manufacturingAction(getToken, 'operator', body))
  const submit = (path: string, body: Record<string, unknown>) =>
    mutate(() => request(getToken, path, json('POST', body)))
  const current = data?.requests.find((r) => r.id === selected) ?? data?.requests[0]
  const offers = data?.offers.filter((o) => o.request_id === current?.id) ?? []
  const label = (r: ManufacturingRecord) =>
    `${r.factory_name ?? r.product ?? ''} · ${r.proposed_quantity ?? r.quantity ?? ''} ${r.unit ?? ''} · ${manufacturingStatusLabel('ko', r.status ?? 'open')}`
  const options = (rows: ManufacturingRecord[]) =>
    rows.map((r) => ({ value: r.id, label: label(r) }))
  const offerField = (rows: ManufacturingRecord[] = offers): WorkflowField => ({
    name: 'offer',
    label: '공장 견적',
    options: options(rows),
  })
  const evidence: WorkflowField = { name: 'evidence', label: '검토 근거', type: 'textarea' }
  const iso = (date: string) => new Date(`${date}T23:59:59`).toISOString()
  const workerOnline = !!state.worker && Date.now() - state.worker.updated_at < 120_000
  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">제조 협력 관리</h1>
        <Link to="/factory" className="underline">
          공장 포털 열기
        </Link>
        <button type="button" onClick={() => void reload().catch(() => undefined)}>
          새로고침
        </button>
      </header>
      {mutationError ? (
        <p role="alert">처리하지 못했습니다. 입력과 최신 상태를 확인하세요.</p>
      ) : null}
      <p role="status">
        {workerOnline ? 'PC 공장관리툴 연결됨' : 'PC 공장관리툴 응답 대기'} · 요청은 서버에
        보관됩니다. 메일 발송·결제·환불 송금은 실행하지 않습니다.
      </p>
      <button
        type="button"
        disabled={pending}
        className="rounded border p-2 disabled:opacity-40"
        onClick={() =>
          void submit('/admin/manufacturing-sync/refresh', {
            operation_id: crypto.randomUUID(),
          }).catch(() => undefined)
        }
      >
        공장 원장 다시 받기
      </button>
      {pending ? <p role="status">작업 처리 중입니다. 확정 결과가 도착하면 변경됩니다.</p> : null}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">고객 제조 요청</h2>
        {!data?.requests.length ? (
          <p>앱에서 접수한 제조 요청이 없습니다.</p>
        ) : (
          <select
            className="w-full rounded border p-2"
            aria-label="고객 제조 요청"
            value={current?.id}
            onChange={(e) => setSelected(e.target.value)}
          >
            {data.requests.map((r) => (
              <option key={r.id} value={r.id}>
                {r.product} · 요청 {r.quantity} {r.unit} ·{' '}
                {manufacturingStatusLabel('ko', r.status ?? 'open')}
              </option>
            ))}
          </select>
        )}
        {current?.external_request_id && !current.external_request_id.startsWith('local:') ? (
          <Link
            className="underline"
            to={`/quotes/${encodeURIComponent(current.external_request_id)}`}
          >
            고객 의뢰서·연락처 확인
          </Link>
        ) : null}
      </section>
      {current ? (
        <Fragment key={current.id}>
          <WorkflowForm
            {...props}
            key={`assign:${current.id}`}
            title="공장 배정"
            fields={[
              {
                name: 'entity',
                label: '공장',
                options: (data?.accounts.filter((a) => a.active) ?? []).map((a) => ({
                  value: a.entity_id,
                  label: a.factory_name,
                })),
              },
              {
                name: 'brief',
                label: '공장에 공개할 제품 설명·범위 (배합·연락처 자동 전송 없음)',
                type: 'textarea',
              },
            ]}
            onSubmit={(v) =>
              action({
                action: 'assign_request',
                request_id: current.id,
                entity_id: v.entity,
                shared_brief: v.brief,
              })
            }
          />
          {data?.assignments
            .filter((a) => a.request_id === current.id && a.active)
            .map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded border p-3">
                <span>{a.factory_name} · 배정됨</span>
                <button
                  disabled={pending}
                  onClick={() =>
                    void action({ action: 'revoke_assignment', assignment_id: a.id }).catch(
                      () => undefined,
                    )
                  }
                >
                  배정 철회
                </button>
              </div>
            ))}
          <WorkflowTable
            title="공장 제출 원본 견적"
            items={offers}
            columns={[
              { label: '공장', value: (r) => r.factory_name },
              { label: '제안 수량', value: (r) => `${r.proposed_quantity} ${r.unit}` },
              { label: '공장 원가 총액', value: (r) => `${r.currency} ${r.amount ?? '미확인'}` },
              { label: '부가세', value: (r) => r.tax_status },
              { label: '공장 유효 기한', value: (r) => fmtTime(r.valid_until) },
              {
                label: '조건·근거',
                value: (r) => (
                  <span className="whitespace-pre-wrap">
                    {r.conditions}
                    <br />
                    {r.source}
                  </span>
                ),
              },
              {
                label: '상태',
                value: (r) => manufacturingStatusLabel('ko', r.status ?? 'pending_review'),
              },
            ]}
          />
          <div className="grid gap-4 lg:grid-cols-2">
            <WorkflowForm
              {...props}
              title="공장 견적 검토 완료"
              fields={[
                offerField(offers.filter((o) => o.status === 'pending_review')),
                {
                  name: 'confirmed',
                  label: '견적 유효성과 부가세·총액을 확인했습니다',
                  type: 'checkbox',
                },
              ]}
              onSubmit={(v) =>
                action({
                  action: 'review_offer',
                  offer_id: v.offer,
                  validity_confirmed: v.confirmed === 'on',
                })
              }
            />
            <WorkflowForm
              {...props}
              title="고객 판매 견적 공개 (공장 원가와 별도)"
              fields={[
                offerField(offers.filter((o) => o.status === 'reviewed')),
                { name: 'amount', label: '고객 최종 총액 (VAT·배송 포함, 원)', type: 'number' },
                { name: 'terms', label: '고객 공개 조건·납기', type: 'textarea' },
                { name: 'until', label: '고객 견적 유효기한', type: 'date' },
              ]}
              onSubmit={(v) =>
                action({
                  action: 'publish_quote',
                  offer_id: v.offer,
                  customer_amount: v.amount,
                  customer_terms: v.terms,
                  valid_until: iso(v.until!),
                })
              }
            />
          </div>
          <WorkflowTable
            title="고객 공개 견적 (판별 보존)"
            items={data?.publications.filter((p) => p.request_id === current.id) ?? []}
            columns={[
              { label: '판', value: (r) => r.version },
              { label: '고객 판매 총액', value: (r) => `${r.customer_amount} ${r.currency}` },
              { label: '고객 조건', value: (r) => r.customer_terms },
              {
                label: '상태',
                value: (r) => manufacturingStatusLabel('ko', r.status ?? 'published'),
              },
              {
                label: '관리',
                value: (r) =>
                  r.status === 'published' ? (
                    <button
                      disabled={pending}
                      onClick={() =>
                        void action({ action: 'withdraw_quote', publication_id: r.id }).catch(
                          () => undefined,
                        )
                      }
                    >
                      공개 철회
                    </button>
                  ) : null,
              },
            ]}
          />
          <WorkflowForm
            {...props}
            title="고객 승인 후 별도 발주 승인"
            fields={[offerField(offers.filter((o) => o.status === 'quote_approved')), evidence]}
            onSubmit={(v) =>
              action({ action: 'approve_order', offer_id: v.offer, evidence: v.evidence })
            }
          />
          <WorkflowTable
            title="주문 진행"
            items={data?.orders.filter((o) => o.request_id === current.id) ?? []}
            columns={[
              { label: '공장', value: (r) => r.factory_name },
              { label: '수량', value: (r) => `${r.quantity} ${r.unit}` },
              { label: '공장 총액', value: (r) => r.amount },
              {
                label: '상태',
                value: (r) => manufacturingStatusLabel('ko', r.status ?? 'approved_not_sent'),
              },
            ]}
          />
          <section className="space-y-4">
            <h2 className="text-lg font-semibold">수수료·입금·환불 기록</h2>
            <p>
              실제 계약과 입금 확인 뒤 기록합니다. 기본 수수료율은 없습니다. 실제 송금은 별도로
              처리하세요.
            </p>
            <WorkflowForm
              {...props}
              title="주문별 공장 수수료 합의"
              fields={[
                {
                  name: 'entity',
                  label: '계약 공장',
                  options: (data?.accounts ?? []).map((a) => ({
                    value: a.entity_id,
                    label: a.factory_name,
                  })),
                },
                { name: 'rate', label: '명시적으로 합의한 수수료율 (%)', type: 'number' },
                { name: 'terms', label: '대상 주문·세금·배송·환불·재주문 범위', type: 'textarea' },
                evidence,
                { name: 'consent', label: '공장 동의 근거를 확인했습니다', type: 'checkbox' },
              ]}
              onSubmit={(v) =>
                action({
                  action: 'create_agreement',
                  request_id: current.id,
                  entity_id: v.entity,
                  rate_percent: v.rate,
                  terms: v.terms,
                  evidence: v.evidence,
                  consent: v.consent === 'on',
                })
              }
            />
            <div className="grid gap-4 lg:grid-cols-2">
              {(['record_receipt', 'refund_receipt'] as const).map((kind) => (
                <WorkflowForm
                  key={kind}
                  {...props}
                  title={kind === 'record_receipt' ? '입금 확인 기록' : '환불 확인 기록'}
                  fields={[
                    offerField(offers.filter((o) => o.status === 'acknowledged')),
                    { name: 'amount', label: '적격 확인 금액 (원)', type: 'number' },
                    { name: 'reference', label: '은행·문서의 고유 거래 식별번호' },
                    evidence,
                  ]}
                  onSubmit={(v) =>
                    action({
                      action: kind,
                      offer_id: v.offer,
                      amount: v.amount,
                      [kind === 'record_receipt' ? 'receipt_ref' : 'refund_ref']: v.reference,
                      evidence: v.evidence,
                    })
                  }
                />
              ))}
            </div>
            <WorkflowForm
              {...props}
              title="수수료 계산"
              fields={[
                offerField(offers.filter((o) => o.status === 'acknowledged')),
                {
                  name: 'agreement',
                  label: '이 주문에 적용할 합의',
                  options: (data?.agreements.filter((a) => a.request_id === current.id) ?? []).map(
                    (a) => ({
                      value: a.id,
                      label: `${a.factory_name} · ${a.rate_percent}% · ${a.terms}`,
                    }),
                  ),
                },
                { name: 'minutes', label: '지원 시간 (분)', type: 'number' },
                { name: 'hourly', label: '시간당 원가 (원)', type: 'number' },
                { name: 'variable', label: '기타 변동비 (원)', type: 'number' },
              ]}
              onSubmit={(v) =>
                action({
                  action: 'calculate_fee',
                  offer_id: v.offer,
                  agreement_id: v.agreement,
                  support_minutes: v.minutes,
                  hourly_cost: v.hourly,
                  variable_cost: v.variable,
                })
              }
            />
          </section>
        </Fragment>
      ) : null}
      <WorkflowTable
        title="정산"
        items={data?.billing ?? []}
        columns={[
          { label: '공장', value: (r) => r.factory_name },
          { label: '주문 식별번호', value: (r) => r.order_id ?? r.offer_id ?? r.id },
          { label: '적격 순수취', value: (r) => r.eligible_receipts },
          { label: '수수료', value: (r) => r.fee_amount },
          { label: '확인된 수수료 수취', value: (r) => r.paid_amount },
          { label: '미수', value: (r) => r.outstanding },
          { label: '반환 예정', value: (r) => r.credit_due },
        ]}
      />
      <WorkflowForm
        {...props}
        title="수수료 수취 확인"
        fields={[
          {
            name: 'billing',
            label: '정산 건',
            options: (data?.billing ?? []).map((b) => ({
              value: b.id,
              label: `${b.factory_name} · ${b.order_id ?? b.offer_id ?? b.id} · ${b.fee_amount}원 · 미수 ${b.outstanding}원`,
            })),
          },
          evidence,
        ]}
        onSubmit={(v) =>
          action({ action: 'mark_fee_paid', billing_id: v.billing, evidence: v.evidence })
        }
      />
      <WorkflowTable
        title="입금·환불 기록"
        items={[...(data?.receipts ?? []), ...(data?.refunds ?? [])]}
        columns={[
          { label: '식별번호', value: (r) => r.receipt_ref ?? r.refund_ref },
          { label: '종류', value: (r) => (r.refund_ref ? '환불' : '입금') },
          { label: '금액', value: (r) => r.amount },
          { label: '확인 시각', value: (r) => r.created_at },
        ]}
      />
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">공장·로그인 계정 연결</h2>
        <p>
          공장 기본 정보와 거래 제한은 PC 공통 원장에서 관리합니다. 등록된 공장에 포털 권한을 만들고
          기존 앱 계정을 연결하세요. 로그인 초대 메일은 자동 발송하지 않습니다.
        </p>
        <WorkflowForm
          {...props}
          title="공장 포털 권한 준비"
          fields={[
            {
              name: 'entity',
              label: '공장 원장',
              options: (
                data?.factories.filter(
                  (f) => !f.restricted && !data.accounts.some((a) => a.entity_id === f.entity_id),
                ) ?? []
              ).map((f) => ({ value: f.entity_id, label: f.factory_name })),
            },
          ]}
          onSubmit={(v) => action({ action: 'register_account', entity_id: v.entity })}
        />
        <WorkflowForm
          {...props}
          title="기존 로그인 계정과 공장 연결"
          fields={[
            {
              name: 'account',
              label: '공장',
              options: (data?.accounts ?? []).map((a) => ({ value: a.id, label: a.factory_name })),
            },
            { name: 'email', label: '앱에 가입한 로그인 이메일' },
          ]}
          onSubmit={(v) =>
            mutate(async () => {
              const found = await adminApi.users.lookup(getToken, v.email!)
              if (found.items.length !== 1) throw new Error('등록된 로그인 계정 1개가 필요합니다.')
              const account = data?.accounts.find((a) => a.id === v.account)
              if (!account) throw new Error('공장을 다시 선택하세요.')
              return request(
                getToken,
                '/admin/manufacturing-sync/accounts',
                json('POST', {
                  operation_id: crypto.randomUUID(),
                  local_account_id: account.id,
                  entity_id: account.entity_id,
                  clerk_user_id: found.items[0]!.userId,
                  active: true,
                }),
              )
            })
          }
        />
        {data?.accounts.map((a) => (
          <div key={a.id} className="flex flex-wrap justify-between gap-3 rounded border p-3">
            <span>
              {a.factory_name} · {a.active ? '활성' : '중지'} ·{' '}
              {state.bindings.some((b) => b.local_account_id === a.id && b.active)
                ? '로그인 연결됨'
                : '로그인 연결 대기'}
            </span>
            <button
              disabled={pending}
              onClick={() =>
                void action({
                  action: 'set_account_active',
                  account_id: a.id,
                  active: !a.active,
                }).catch(() => undefined)
              }
            >
              {a.active ? '권한 중지' : '권한 재개'}
            </button>
          </div>
        ))}
      </section>
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">처리 기록</h2>
        {state.jobs.map((j) => (
          <div key={j.id} className="flex flex-wrap gap-4 border-b p-2">
            <span>
              #{j.sequence} · {manufacturingStatusLabel('ko', j.status)}
              {j.error_code ? ` · ${j.error_code}` : ''}
            </span>
            {j.status === 'failed' && j.error_code === 'SYNC_FAILED' ? (
              <button
                disabled={pending}
                onClick={() =>
                  void submit(`/admin/manufacturing-sync/jobs/${j.id}/retry`, {
                    operation_id: crypto.randomUUID(),
                  }).catch(() => undefined)
                }
              >
                중단된 작업 다시 처리
              </button>
            ) : null}
          </div>
        ))}
      </section>
    </div>
  )
}
